import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ReportConfiguration = ({ reportType, onConfigChange, config }) => {
  const [validationErrors, setValidationErrors] = useState({});

  const vesselOptions = [
    { value: 'all', label: 'All Vessels (12 vessels)' },
    { value: 'mv-atlantic-voyager', label: 'MV Atlantic Voyager (IMO: 9876543)' },
    { value: 'mv-pacific-trader', label: 'MV Pacific Trader (IMO: 9876544)' },
    { value: 'mv-nordic-explorer', label: 'MV Nordic Explorer (IMO: 9876545)' },
    { value: 'mv-southern-star', label: 'MV Southern Star (IMO: 9876546)' },
    { value: 'mv-eastern-wind', label: 'MV Eastern Wind (IMO: 9876547)' }
  ];

  const jurisdictionOptions = [
    { value: 'eu', label: 'European Union' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'norway', label: 'Norway' },
    { value: 'iceland', label: 'Iceland' }
  ];

  const reportingPeriodOptions = [
    { value: '2024', label: '2024 (Full Year)' },
    { value: '2024-q4', label: '2024 Q4 (Oct-Dec)' },
    { value: '2024-q3', label: '2024 Q3 (Jul-Sep)' },
    { value: '2024-q2', label: '2024 Q2 (Apr-Jun)' },
    { value: '2024-q1', label: '2024 Q1 (Jan-Mar)' }
  ];

  const formatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'xml', label: 'XML (Regulatory Submission)' },
    { value: 'json', label: 'JSON Data Export' }
  ];

  const handleConfigUpdate = (field, value) => {
    const updatedConfig = { ...config, [field]: value };
    onConfigChange(updatedConfig);
    
    // Clear validation error for this field
    if (validationErrors?.[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateConfiguration = () => {
    const errors = {};
    
    if (!config?.vessels || config?.vessels?.length === 0) {
      errors.vessels = 'Please select at least one vessel';
    }
    
    if (!config?.reportingPeriod) {
      errors.reportingPeriod = 'Please select a reporting period';
    }
    
    if (!config?.jurisdiction) {
      errors.jurisdiction = 'Please select a jurisdiction';
    }

    setValidationErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const getReportTypeConfig = () => {
    switch (reportType) {
      case 'eu-ets':
        return {
          title: 'EU ETS Surrender Report Configuration',
          description: 'Configure parameters for EU ETS compliance reporting',
          specificFields: ['installationId', 'verificationBody']
        };
      case 'fueleu':
        return {
          title: 'FuelEU Maritime Configuration',
          description: 'Set parameters for FuelEU Maritime compliance statement',
          specificFields: ['complianceRoute', 'alternativeFuels']
        };
      case 'imo-dcs':
        return {
          title: 'IMO DCS Report Configuration',
          description: 'Configure IMO Data Collection System report parameters',
          specificFields: ['flagState', 'shipType']
        };
      case 'cii-evidence':
        return {
          title: 'CII Evidence Pack Configuration',
          description: 'Set parameters for Carbon Intensity Indicator documentation',
          specificFields: ['ciiRating', 'improvementMeasures']
        };
      default:
        return {
          title: 'Report Configuration',
          description: 'Configure report parameters',
          specificFields: []
        };
    }
  };

  const reportConfig = getReportTypeConfig();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">{reportConfig?.title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{reportConfig?.description}</p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={validateConfiguration}
          iconName="CheckCircle"
          iconPosition="left"
        >
          Validate Config
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Basic Parameters</h3>
          
          <Select
            label="Vessels"
            description="Select vessels to include in the report"
            options={vesselOptions}
            value={config?.vessels || []}
            onChange={(value) => handleConfigUpdate('vessels', value)}
            multiple
            searchable
            error={validationErrors?.vessels}
            required
          />

          <Select
            label="Reporting Period"
            description="Select the time period for the report"
            options={reportingPeriodOptions}
            value={config?.reportingPeriod || ''}
            onChange={(value) => handleConfigUpdate('reportingPeriod', value)}
            error={validationErrors?.reportingPeriod}
            required
          />

          <Select
            label="Jurisdiction"
            description="Select the regulatory jurisdiction"
            options={jurisdictionOptions}
            value={config?.jurisdiction || ''}
            onChange={(value) => handleConfigUpdate('jurisdiction', value)}
            error={validationErrors?.jurisdiction}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              value={config?.startDate || '2024-01-01'}
              onChange={(e) => handleConfigUpdate('startDate', e?.target?.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={config?.endDate || '2024-12-31'}
              onChange={(e) => handleConfigUpdate('endDate', e?.target?.value)}
            />
          </div>
        </div>

        {/* Advanced Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Export & Format</h3>
          
          <Select
            label="Export Format"
            description="Choose the output format for the report"
            options={formatOptions}
            value={config?.format || 'pdf'}
            onChange={(value) => handleConfigUpdate('format', value)}
          />

          <Input
            label="Report Title"
            type="text"
            placeholder="Enter custom report title"
            value={config?.title || ''}
            onChange={(e) => handleConfigUpdate('title', e?.target?.value)}
            description="Optional custom title for the report"
          />

          <Input
            label="Reference Number"
            type="text"
            placeholder="Enter reference number"
            value={config?.reference || ''}
            onChange={(e) => handleConfigUpdate('reference', e?.target?.value)}
            description="Internal reference or tracking number"
          />

          {/* Report Type Specific Fields */}
          {reportType === 'eu-ets' && (
            <>
              <Input
                label="Installation ID"
                type="text"
                placeholder="EU ETS Installation ID"
                value={config?.installationId || ''}
                onChange={(e) => handleConfigUpdate('installationId', e?.target?.value)}
                description="Your EU ETS installation identifier"
              />
              <Input
                label="Verification Body"
                type="text"
                placeholder="Accredited verification body"
                value={config?.verificationBody || ''}
                onChange={(e) => handleConfigUpdate('verificationBody', e?.target?.value)}
                description="Name of the accredited verifier"
              />
            </>
          )}

          {reportType === 'fueleu' && (
            <>
              <Select
                label="Compliance Route"
                options={[
                  { value: 'pooling', label: 'Pooling Mechanism' },
                  { value: 'individual', label: 'Individual Compliance' },
                  { value: 'banking', label: 'Banking & Borrowing' }
                ]}
                value={config?.complianceRoute || ''}
                onChange={(value) => handleConfigUpdate('complianceRoute', value)}
                description="FuelEU compliance strategy"
              />
            </>
          )}
        </div>
      </div>
      {/* Data Validation Status */}
      <div className="maritime-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Data Validation Status</h4>
          <Button variant="ghost" size="sm" iconName="RefreshCw" iconPosition="left">
            Refresh
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <Icon name="CheckCircle" size={16} strokeWidth={2} className="text-success" />
            <div>
              <div className="text-sm font-medium text-foreground">Emissions Data</div>
              <div className="text-xs text-muted-foreground">Complete & Verified</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Icon name="AlertTriangle" size={16} strokeWidth={2} className="text-warning" />
            <div>
              <div className="text-sm font-medium text-foreground">EUA Transactions</div>
              <div className="text-xs text-muted-foreground">2 pending verifications</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Icon name="CheckCircle" size={16} strokeWidth={2} className="text-success" />
            <div>
              <div className="text-sm font-medium text-foreground">Voyage Records</div>
              <div className="text-xs text-muted-foreground">All records available</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportConfiguration;